import type { Dispatch, SetStateAction } from 'react';
import { createContext, useMemo, useState } from 'react';

import type { Price } from '~/interfaces/subscriptions';

export type ContextType = {
  donationAmountCents: number;
  setDonationAmountCents: Dispatch<SetStateAction<number>>;
  selectedPrice: Price | null;
  setSelectedPrice: Dispatch<SetStateAction<Price | null>>;
  handleDonationAmountCentsChanged: (value: number) => void;
};

const PricingContext = createContext<ContextType | undefined>(undefined);
PricingContext.displayName = 'PricingContext';

const translateDefaultAmount = (amount: string) => {
  try {
    return Number.parseInt(amount) * 100;
  } catch (_e) {
    return 100;
  }
};

const PricingProvider = ({
  children,
  nameYourPriceDefaultAmount,
}: {
  children: React.ReactNode;
  nameYourPriceDefaultAmount: string;
}) => {
  const [donationAmountCents, setDonationAmountCents] = useState<number>(
    translateDefaultAmount(nameYourPriceDefaultAmount),
  );
  const [selectedPrice, setSelectedPrice] = useState<Price | null>(null);

  const handleDonationAmountCentsChanged = (value: number) => {
    setDonationAmountCents(value);
  };

  const context: ContextType = useMemo(
    () => ({
      donationAmountCents,
      setDonationAmountCents,
      selectedPrice,
      setSelectedPrice,
      handleDonationAmountCentsChanged,
    }),
    [donationAmountCents, setDonationAmountCents, selectedPrice, setSelectedPrice],
  );

  return <PricingContext.Provider value={context}>{children}</PricingContext.Provider>;
};

export { PricingContext, PricingProvider };
